import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../hooks";
import { ROUTES } from "../../constants";
import { validators } from "../../utils/validators.util";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { AuthPageChrome } from "../../components/auth/AuthPageChrome";
import {
  Brain,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Sparkles,
  Shield,
  Target,
  Zap,
  UserPlus,
  Building2,
} from "lucide-react";

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: SPRING } },
};

const inputClass =
  "h-12 rounded-xl border-white/[0.10] bg-white/[0.04] pl-11 text-white placeholder:text-slate-600 ring-1 ring-inset ring-white/[0.07] transition-all duration-200 focus-visible:border-teal-500/60 focus-visible:bg-white/[0.07] focus-visible:ring-teal-500/30 focus-visible:shadow-[0_0_0_3px_rgba(45,212,191,0.10)]";

const Login = observer(function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validators.email(email)) {
      setError("Por favor, insira um e-mail válido");
      return;
    }

    if (!validators.password(password)) {
      setError("A senha deve ter pelo menos 8 caracteres");
      return;
    }

    setIsLoading(true);

    try {
      await login({ email, password });
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao fazer login. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const highlights = [
    {
      icon: Brain,
      title: "Perfil sem caixa-preta",
      text: "Questionário e comportamento geram um perfil canônico com regras auditáveis.",
    },
    {
      icon: Zap,
      title: "Ritmo que reage",
      text: "Apresentação e cadência ajustam conforme acertos, erros e tempo — limiares configuráveis.",
    },
    {
      icon: Target,
      title: "Camadas separadas",
      text: "Gamificação e estudo de um lado; notas formais e institucional de outro.",
    },
    {
      icon: Shield,
      title: "LGPD na base",
      text: "Dados separados por papel, transparência total e motivação desvinculada da avaliação.",
    },
  ];

  return (
    <AuthPageChrome>
      {/* Extra depth glow behind the form card */}
      <div
        aria-hidden
        className="pointer-events-none fixed right-0 top-1/2 -translate-y-1/2 h-[600px] w-[520px] opacity-20"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(45,212,191,0.18) 0%, rgba(6,182,212,0.10) 35%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="relative z-10 container mx-auto max-w-7xl px-4 pb-14 pt-8 sm:px-6 sm:pb-20 sm:pt-10">
        <div className="grid items-start gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:items-center">

          {/* ── Left column ──────────────────────────────────────────── */}
          <motion.div
            className="hidden min-w-0 lg:flex lg:flex-col lg:gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <span className="badge-glow inline-flex items-center gap-1.5 rounded-full border border-teal-400/30 bg-teal-500/10 px-3 py-1 text-xs font-medium text-teal-200">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                Acesso à plataforma
              </span>
            </motion.div>

            <motion.div variants={itemVariants} className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 shadow-lg shadow-teal-500/25">
                <Brain className="h-5 w-5 text-white" aria-hidden />
              </div>
              <div>
                <p className="font-display text-lg font-semibold tracking-tight text-white">
                  NeuroMent
                </p>
                <p className="text-sm text-teal-300/80">
                  Personalização por perfil · gestão institucional
                </p>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h1 className="font-display text-[2rem] font-bold leading-[1.12] tracking-tight text-white lg:text-[2.6rem]">
                Bem-vindo de volta
                <span className="mt-1.5 block bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent">
                  Continue sua jornada
                </span>
              </h1>

              {/* decorative rule */}
              <div className="mt-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-teal-400/40 to-transparent" />
                <span className="text-xs text-slate-600">NeuroMent · 2026</span>
              </div>
            </motion.div>

            <motion.p variants={itemVariants} className="max-w-lg text-base leading-relaxed text-slate-400">
              Entre com e-mail e senha. Escolas e redes usam o mesmo acesso quando a
              implementação institucional estiver ativa na sua unidade.
            </motion.p>

            <motion.ul variants={containerVariants} className="space-y-3">
              {highlights.map((item) => (
                <motion.li
                  key={item.title}
                  variants={itemVariants}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-teal-500/20 bg-teal-500/[0.08]">
                    <item.icon className="h-4 w-4 text-teal-400" aria-hidden />
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-sm font-medium text-slate-200">{item.title}</p>
                    <p className="mt-0.5 text-sm leading-snug text-slate-500">{item.text}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* ── Form card ─────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: SPRING, delay: 0.08 }}
          >
            <div className="relative overflow-hidden rounded-2xl border border-white/[0.09] bg-gradient-to-b from-[#0d1e30]/85 to-[#070d18]/90 p-7 shadow-[0_32px_80px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(45,212,191,0.06),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-2xl sm:p-9">

              {/* Animated top edge highlight */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(45,212,191,0.55) 40%, rgba(6,182,212,0.45) 60%, transparent 100%)",
                }}
              />

              {/* Subtle corner glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20"
                style={{ background: "radial-gradient(circle, rgba(45,212,191,0.25) 0%, transparent 70%)" }}
              />

              <motion.div
                className="relative space-y-6"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Card header */}
                <motion.div variants={itemVariants} className="space-y-1 text-center lg:text-left">
                  <div className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] bg-white/[0.05] px-2.5 py-1 text-xs text-slate-400 lg:hidden">
                    <Brain className="h-3.5 w-3.5 text-teal-400" aria-hidden />
                    NeuroMent
                  </div>
                  <h2 className="font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">
                    Entrar na sua conta
                  </h2>
                  <p className="text-sm leading-relaxed text-slate-500 sm:text-[0.9rem]">
                    Use suas credenciais. Primeiro acesso? Crie conta de estudante ou siga o fluxo indicado pela escola.
                  </p>
                </motion.div>

                {/* Form */}
                <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="rounded-xl border border-red-500/30 bg-red-500/[0.08] px-4 py-3 text-sm text-red-300"
                    >
                      {error}
                    </motion.div>
                  )}

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-300">
                      E-mail
                    </Label>
                    <div className="relative">
                      <Mail
                        className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4.5 w-4.5 -translate-y-1/2 text-slate-500"
                        style={{ width: "1.05rem", height: "1.05rem" }}
                        aria-hidden
                      />
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(null); }}
                        className={inputClass}
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium text-slate-300">
                      Senha
                    </Label>
                    <div className="relative">
                      <Lock
                        className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-500"
                        style={{ width: "1.05rem", height: "1.05rem" }}
                        aria-hidden
                      />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(null); }}
                        className={`${inputClass} pr-11`}
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-500 transition-colors hover:text-slate-300 focus-visible:outline-none focus-visible:text-slate-200"
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Remember / forgot */}
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <label className="flex cursor-pointer items-center gap-2 text-slate-500 transition-colors hover:text-slate-300">
                      <input
                        type="checkbox"
                        className="rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/30"
                      />
                      <span>Lembrar-me</span>
                    </label>
                    <button
                      type="button"
                      className="text-teal-400/80 transition-colors hover:text-teal-300 focus-visible:outline-none focus-visible:text-teal-200"
                    >
                      Esqueceu a senha?
                    </button>
                  </div>

                  {/* PRIMARY CTA — login */}
                  <Button
                    type="submit"
                    size="lg"
                    className="landing-cta-glow h-12 w-full rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-sm font-semibold shadow-xl shadow-teal-950/50 hover:from-teal-400 hover:to-cyan-500 sm:h-14 sm:text-base"
                    disabled={isLoading}
                  >
                    {isLoading ? "Entrando…" : "Entrar"}
                  </Button>
                </motion.form>

                {/* Divider */}
                <motion.div variants={itemVariants} className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/[0.08]" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="rounded-md bg-[#0d1e30] px-3 py-0.5 text-slate-600 ring-1 ring-inset ring-white/[0.06]">
                      Primeiro acesso
                    </span>
                  </div>
                </motion.div>

                {/* SECONDARY CTAs — new users */}
                <motion.div variants={itemVariants} className="flex flex-col gap-3">

                  {/* Institutional signup — neutral, professional */}
                  <Button
                    type="button"
                    onClick={() => navigate("/cadastro-instituicao")}
                    size="lg"
                    className="h-12 w-full rounded-xl border border-white/[0.13] bg-white/[0.04] text-sm font-medium text-slate-300 transition-all duration-200 hover:border-white/[0.22] hover:bg-white/[0.08] hover:text-white sm:h-14 sm:text-base"
                  >
                    <Building2 className="mr-2 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                    Cadastrar instituição
                  </Button>

                  {/* Pilot signup — teal-tinted invitation, clearly visible */}
                  <Button
                    type="button"
                    onClick={() => navigate("/signup")}
                    size="lg"
                    className="group h-12 w-full rounded-xl border border-teal-500/40 bg-teal-500/[0.08] text-sm font-medium text-teal-200 shadow-[0_0_20px_-10px_rgba(45,212,191,0.4)] transition-all duration-200 hover:border-teal-400/65 hover:bg-teal-500/[0.15] hover:text-teal-100 hover:shadow-[0_0_28px_-8px_rgba(45,212,191,0.55)] sm:h-14 sm:text-base"
                  >
                    <UserPlus className="mr-2 h-4 w-4 shrink-0 transition-transform duration-200 group-hover:scale-110" aria-hidden />
                    Criar conta de estudante
                    <span className="ml-2 rounded-full bg-teal-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-300 ring-1 ring-teal-400/30">
                      Piloto
                    </span>
                  </Button>
                </motion.div>

                {/* Footer note */}
                <motion.p
                  variants={itemVariants}
                  className="text-center text-[11px] leading-relaxed text-slate-600 sm:text-xs"
                >
                  Ao continuar, você aceita os termos aplicáveis ao seu tipo de acesso.{" "}
                  <Link
                    to={ROUTES.HOME}
                    className="text-teal-500/70 transition-colors hover:text-teal-400"
                  >
                    Voltar à página inicial
                  </Link>
                </motion.p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </AuthPageChrome>
  );
});

export default Login;
